import { useEffect } from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Controller,
  useForm,
} from "react-hook-form";

import {
  CloseTicketPayload,
  ServiceTicket,
} from "../types";

export interface SpareClosureLine {
  sparePartId: string;
  name: string;
  code: string;
  issuedQty: number;
  consumedQty: number;
  returnedQty: number;
  acceptedQty: number;
  damagedQty: number;
}

interface CloseTicketDialogProps {
  open: boolean;

  loading?: boolean;

  ticket?: ServiceTicket | null;

  spareCost?: number;

  spareSummary?: SpareClosureLine[];

  onClose: () => void;

  onSubmit: (
    payload: CloseTicketPayload
  ) => void;
}

const defaultValues: CloseTicketPayload = {
  actualCost: undefined,

  resolution: "",

  remarks: "",
};

const CloseTicketDialog = ({
  open,
  loading = false,
  ticket,
  spareCost = 0,
  spareSummary = [],
  onClose,
  onSubmit,
}: CloseTicketDialogProps) => {
  const isCompletionStep =
    !!ticket &&
    ["IN_PROGRESS", "SPARE_ISSUED", "WAITING_FOR_PARTS"].includes(ticket.status);

  const {
    control,
    handleSubmit,
    reset,
    watch,
  } =
    useForm<CloseTicketPayload>({
      defaultValues,
    });

  useEffect(() => {
    reset({
      ...defaultValues,
      actualCost:
        ticket?.actualRepairCost === null || ticket?.actualRepairCost === undefined
          ? undefined
          : Number(ticket.actualRepairCost),
    });
  }, [ticket, reset]);

  const actualCost = Number(watch("actualCost") || 0);
  const totalMaintenanceCost = Number(spareCost || 0) + actualCost;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isCompletionStep ? "Complete Service Work" : "Close Service Ticket"}
      </DialogTitle>

      <DialogContent dividers>
        <Stack
          spacing={3}
          sx={{ mt: 1 }}
        >
          {ticket && (
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Ticket Number
                </Typography>

                <Typography
                  fontWeight={600}
                >
                  {ticket.ticketNumber}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Vehicle
                </Typography>

                <Typography
                  fontWeight={600}
                >
                  {ticket.vehicleNumber}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Technician
                </Typography>

                <Typography
                  fontWeight={600}
                >
                  {ticket.assignedTechnicianName ||
                    "-"}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Priority
                </Typography>

                <Typography
                  fontWeight={600}
                >
                  {ticket.priority}
                </Typography>
              </Grid>
            </Grid>
          )}

          {spareSummary.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={700}>
                Spare Quantity Summary
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Spare Part</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Consumed</TableCell>
                    <TableCell align="right">Returned</TableCell>
                    <TableCell align="right">Accepted</TableCell>
                    <TableCell align="right">Damaged</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spareSummary.map((item) => (
                    <TableRow key={item.sparePartId}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.code}</TableCell>
                      <TableCell align="right">{item.issuedQty}</TableCell>
                      <TableCell align="right">{item.consumedQty}</TableCell>
                      <TableCell align="right">{item.returnedQty}</TableCell>
                      <TableCell align="right">{item.acceptedQty}</TableCell>
                      <TableCell align="right">{item.damagedQty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          )}

          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Controller
                name="actualCost"
                control={control}
                rules={
                  isCompletionStep
                    ? {
                        required: "Enter the actual repair cost before completing work.",
                        min: {
                          value: 0,
                          message: "Actual repair cost cannot be negative.",
                        },
                      }
                    : undefined
                }
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Actual Repair Cost"
                    disabled={!isCompletionStep}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ||
                      (isCompletionStep
                        ? "Enter the repair or labour cost recorded by the assigned technician."
                        : "Recorded by the assigned technician when work was completed.")
                    }
                    inputProps={{
                      min: 0,
                    }}
                  />
                )}
              />
            </Grid>

            {!isCompletionStep && (
              <>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Consumed Spare Cost
                  </Typography>
                  <Typography fontWeight={700}>
                    Rs {Number(spareCost || 0).toLocaleString("en-IN")}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Maintenance Cost
                  </Typography>
                  <Typography fontWeight={700}>
                    Rs {totalMaintenanceCost.toLocaleString("en-IN")}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid
              item
              xs={12}
            >
              <Controller
                name="resolution"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={4}
                    label={isCompletionStep ? "Completion Notes" : "Resolution"}
                    placeholder={
                      isCompletionStep
                        ? "Describe the completed repair work..."
                        : "Describe how the issue was resolved..."
                    }
                  />
                )}
              />
            </Grid>
                        <Grid
              item
              xs={12}
            >
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    label="Remarks"
                    placeholder="Enter additional remarks..."
                  />
                )}
              />
            </Grid>
          </Grid>

          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
            >
              Completion Checklist
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked
                  disabled
                />
              }
              label="Vehicle repaired successfully"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked
                  disabled
                />
              }
              label="Spare parts installed"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked
                  disabled
                />
              }
              label="Vehicle tested"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked
                  disabled
                />
              }
              label="Ready for deployment"
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit(onSubmit)}
          disabled={
            loading || !ticket
          }
        >
          {loading
            ? isCompletionStep
              ? "Completing..."
              : "Closing..."
            : isCompletionStep
              ? "Complete Work"
              : "Close Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseTicketDialog;
